import { useId, useRef, useState, type ClipboardEvent, type DragEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { ClipboardPaste, ImagePlus, X } from "lucide-react";
import { ApiError, extractErrorMessage } from "@/api/client";
import {
  listingAgentPasteApi,
  type ListingAgentPasteCandidateDto,
  type ListingAgentPasteExtractPayload,
  type ListingAgentPasteExtractionDto,
} from "@/api/listingAgentPaste";
import { Button, Textarea } from "@/design-system/components/ui";
import { formatNumber } from "@/design-system/lib/format";
import {
  NOTHING_FOUND_MESSAGE,
  PASTE_IMAGE_TYPES,
  pasteImageProblem,
  pasteTextProblem,
  RATE_LIMITED_MESSAGE,
  readFileAsBase64,
} from "./pasteLimits";

/** What PASTE TO FILL hands its host: the one person chosen, plus what the host
 *  needs to merge and mark them. */
export interface AgentPasteChoice {
  candidate: ListingAgentPasteCandidateDto;
  /** The model's own note, offered for Notes by the host. */
  notesLine: string | null;
  /** Read from an image: every value is marked "read from image — check it". */
  fromImage: boolean;
  /** Values the server left out (not in the paste, or not valid). */
  droppedValueCount: number;
}

export interface AgentPasteToFillProps {
  /** Called with the person to fill from. With several people, only after the
   *  specialist has picked one (D4); never with nobody. */
  onPick: (choice: AgentPasteChoice) => void;
  disabled?: boolean;
  /** Controlled open state. Omit both for the self-contained button + panel. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Hide the built-in PASTE TO FILL button; the host renders its own
   *  (`PasteToFillButton`) where it belongs. */
  hideTrigger?: boolean;
  testIdPrefix?: string;
}

/** The PASTE TO FILL trigger, for a host that places it itself (beside
 *  + NEW AGENT in the Edit listing agent modal). */
export function PasteToFillButton({
  onClick,
  disabled,
  "data-testid": testId = "agent-paste-open",
}: {
  onClick: () => void;
  disabled?: boolean;
  "data-testid"?: string;
}) {
  return (
    <Button type="button" variant="secondary" size="sm" onClick={onClick} disabled={disabled} data-testid={testId}>
      <ClipboardPaste size={14} strokeWidth={1.5} aria-hidden /> Paste to fill
    </Button>
  );
}

/**
 * PASTE TO FILL (D1–D6) — the shared paste box. Accepts typed or pasted text,
 * an image pasted from the clipboard, dropped on the box, or chosen from disk
 * (PNG, JPEG or WebP, one, ≤ 5 MB), reads it through the server, and hands the
 * chosen person to the host.
 *
 * <p>🔴 <b>It writes nothing and fills nothing itself.</b> Every host decides
 * what a choice means: the New listing agent form merges into its blank fields;
 * the Edit listing agent modal runs the duplicate check first; the research
 * workspace turns it into findings. That is what keeps it reusable — no props
 * about forms, listings or agents.</p>
 *
 * <p>The paste survives every failure: a refusal, a 422 or a reply naming
 * nobody leaves the box as it was, so the specialist can copy more and try
 * again. It clears only once a person has been handed over.</p>
 */
export function AgentPasteToFill({
  onPick,
  disabled = false,
  open: openProp,
  onOpenChange,
  hideTrigger = false,
  testIdPrefix = "agent-paste",
}: AgentPasteToFillProps) {
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = (next: boolean) => {
    if (openProp === undefined) setOpenState(next);
    onOpenChange?.(next);
  };

  const [text, setText] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [choices, setChoices] = useState<ListingAgentPasteExtractionDto | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const textId = useId();

  const reset = () => {
    setText("");
    setImage(null);
    setProblem(null);
    setNotice(null);
    setChoices(null);
  };

  const close = () => {
    reset();
    setOpen(false);
  };

  const hand = (result: ListingAgentPasteExtractionDto, candidate: ListingAgentPasteCandidateDto) => {
    onPick({
      candidate,
      notesLine: result.notesLine ?? null,
      fromImage: result.fromImage,
      droppedValueCount: result.droppedValueCount,
    });
    close();
  };

  const extract = useMutation({
    mutationFn: async (): Promise<ListingAgentPasteExtractionDto> => {
      const body: ListingAgentPasteExtractPayload = image
        ? { imageBase64: await readFileAsBase64(image), imageMediaType: image.type }
        : { text: text.trim() };
      return listingAgentPasteApi.extract(body);
    },
    // no-invalidation: reads a paste into a prefill; the server writes only its AI ledger row, which no cached read serves.
    onSuccess: (result) => {
      if (result.people.length === 0) {
        setNotice(NOTHING_FOUND_MESSAGE);
        return;
      }
      if (result.people.length === 1) {
        hand(result, result.people[0]);
        return;
      }
      setChoices(result);
    },
    onError: (e: unknown) => {
      setProblem(e instanceof ApiError && e.status === 429 ? RATE_LIMITED_MESSAGE : extractErrorMessage(e));
    },
  });

  const busy = disabled || extract.isPending;

  const takeImage = (file: File) => {
    const refusal = pasteImageProblem(file);
    setNotice(null);
    setChoices(null);
    if (refusal) {
      setProblem(refusal);
      return;
    }
    setProblem(null);
    setImage(file);
  };

  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const file = Array.from(e.clipboardData?.files ?? [])[0];
    if (!file) return;
    // An image on the clipboard is the paste; the box keeps any text typed so
    // far for if the image is removed.
    e.preventDefault();
    takeImage(file);
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    const file = Array.from(e.dataTransfer?.files ?? [])[0];
    if (!file) return;
    e.preventDefault();
    takeImage(file);
  };

  const submit = () => {
    setNotice(null);
    setChoices(null);
    const refusal = image ? pasteImageProblem(image) : pasteTextProblem(text);
    if (refusal) {
      setProblem(refusal);
      return;
    }
    setProblem(null);
    extract.mutate();
  };

  if (!open) {
    return hideTrigger ? null : (
      <div>
        <PasteToFillButton onClick={() => setOpen(true)} disabled={disabled} data-testid={`${testIdPrefix}-open`} />
      </div>
    );
  }

  return (
    <div
      className="grid gap-space-2 rounded-md border border-border-light bg-white-warm px-space-3 py-space-3"
      data-testid={`${testIdPrefix}-panel`}
      onDragOver={(e) => {
        if (e.dataTransfer?.types?.includes("Files")) e.preventDefault();
      }}
      onDrop={onDrop}
    >
      <label htmlFor={textId} className="text-small font-semibold text-onyx">
        Paste the agent&apos;s details
      </label>
      <p className="text-caption text-gray-mid m-0">
        Copy a broker profile, team page, listing page or email signature, or paste a screenshot.
        Nothing is saved until you save the agent.
      </p>

      {image ? (
        <div
          className="flex flex-wrap items-center justify-between gap-space-2 rounded-md border border-border-light bg-white-pure px-space-3 py-space-2"
          data-testid={`${testIdPrefix}-image`}
        >
          <span className="text-small text-onyx break-all">
            {image.name || "Pasted image"} · {formatNumber(Math.max(1, Math.round(image.size / 1024)))} KB
          </span>
          <Button
            type="button"
            variant="text"
            size="sm"
            onClick={() => {
              setImage(null);
              setProblem(null);
              setNotice(null);
            }}
            disabled={busy}
            data-testid={`${testIdPrefix}-remove-image`}
          >
            <X size={13} strokeWidth={1.5} aria-hidden /> Remove image
          </Button>
        </div>
      ) : (
        <Textarea
          id={textId}
          rows={6}
          value={text}
          disabled={busy}
          onChange={(e) => {
            setText(e.target.value);
            setProblem(null);
            setNotice(null);
          }}
          onPaste={onPaste}
          placeholder="Paste text here, or paste or drop a screenshot"
          data-testid={`${testIdPrefix}-text`}
        />
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={PASTE_IMAGE_TYPES.join(",")}
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) takeImage(file);
          e.target.value = "";
        }}
        data-testid={`${testIdPrefix}-file`}
      />

      {problem && (
        <p className="text-small text-status-error m-0" role="alert" data-testid={`${testIdPrefix}-problem`}>
          {problem}
        </p>
      )}
      {notice && (
        <p className="text-small text-onyx m-0" role="status" data-testid={`${testIdPrefix}-notice`}>
          {notice}
        </p>
      )}

      {choices ? (
        <div className="grid gap-space-2" data-testid={`${testIdPrefix}-choices`}>
          <p className="text-small font-semibold text-onyx m-0">Which of these is this listing&apos;s agent?</p>
          {choices.peopleTruncated && (
            <p className="text-caption text-gray-mid m-0">The paste names more than ten people; the first ten are shown.</p>
          )}
          <ul className="m-0 p-0 list-none grid gap-space-2">
            {choices.people.map((person, i) => (
              <li
                key={i}
                className="flex flex-wrap items-start justify-between gap-space-2 rounded-md border border-border-light bg-white-pure px-space-3 py-space-2"
              >
                <span className="grid min-w-0 gap-[2px]">
                  <span className="text-small text-onyx">{personLabel(person)}</span>
                  {personDetail(person) && (
                    <span className="text-caption text-gray-mid break-all">{personDetail(person)}</span>
                  )}
                </span>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => hand(choices, person)}
                  aria-label={`Use ${personLabel(person)}`}
                  data-testid={`${testIdPrefix}-choose-${i}`}
                >
                  Use this person
                </Button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-end gap-space-2">
        {!image && (
          <Button
            type="button"
            variant="text"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
            data-testid={`${testIdPrefix}-choose-image`}
          >
            <ImagePlus size={14} strokeWidth={1.5} aria-hidden /> Choose image
          </Button>
        )}
        <Button type="button" variant="text" size="sm" onClick={close} disabled={extract.isPending}>
          Cancel paste
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={submit}
          loading={extract.isPending}
          disabled={busy}
          data-testid={`${testIdPrefix}-read`}
        >
          {extract.isPending ? "Reading…" : "Read paste"}
        </Button>
      </div>
    </div>
  );
}

/** The person's name, else "Firm contact — {Firm}", else what identifies them. */
export function personLabel(p: ListingAgentPasteCandidateDto): string {
  const name = [p.firstName, p.lastName].filter((s) => s?.trim()).join(" ");
  if (name) return name;
  if (p.firmName?.trim()) return `Firm contact — ${p.firmName.trim()}`;
  return p.email?.trim() || p.phone?.trim() || p.firmGeneralEmail?.trim() || "Unnamed contact";
}

function personDetail(p: ListingAgentPasteCandidateDto): string {
  const name = [p.firstName, p.lastName].filter((s) => s?.trim()).join(" ");
  return [p.title, name ? p.firmName : null, p.email, p.phone]
    .map((s) => s?.trim())
    .filter(Boolean)
    .join(" · ");
}
